import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { MagnifyingGlassIcon, FunnelIcon } from '@heroicons/react/24/outline';

import api from '../services/api';
import LoadingSpinner from '../components/ui/LoadingSpinner';

const Products = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    category: searchParams.get('category') || '',
    minPrice: searchParams.get('minPrice') || '',
    maxPrice: searchParams.get('maxPrice') || '',
    sort: searchParams.get('sort') || '-createdAt'
  });
  const [showFilters, setShowFilters] = useState(false);

  const page = parseInt(searchParams.get('page')) || 1;
  const limit = 12;

  // جلب المنتجات
  const { data: productsData, isLoading } = useQuery(
    ['products', page, filters],
    () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
      });
      return api.get(`/products?${params}`).then(res => res.data.data);
    }
  );

  // جلب الفئات
  const { data: categories } = useQuery(
    'categories',
    () => api.get('/categories').then(res => res.data.data)
  );

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    
    // تحديث URL
    const newParams = new URLSearchParams();
    Object.entries(newFilters).forEach(([k, v]) => {
      if (v) newParams.set(k, v);
    });
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const handlePageChange = (newPage) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', newPage.toString());
    setSearchParams(newParams);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const products = productsData?.products || [];
  const pagination = productsData?.pagination || {};

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">المنتجات</h1>
          <p className="text-gray-600">
            {pagination.total ? `${pagination.total} منتج` : 'لا توجد منتجات'}
          </p>
        </div>
        
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="md:hidden flex items-center mt-4 px-4 py-2 border border-gray-300 rounded-lg"
        >
          <FunnelIcon className="w-5 h-5 ml-2" />
          الفلاتر
        </button>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Filters Sidebar */}
        <div className={`lg:w-64 ${showFilters ? 'block' : 'hidden lg:block'}`}>
          <div className="bg-white rounded-lg shadow-md p-6 sticky top-4">
            <h3 className="text-lg font-semibold mb-4">البحث والفلترة</h3>
            
            {/* Search */}
            <div className="mb-6">
              <label className="form-label">البحث</label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="ابحث عن المنتجات..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="form-input pl-10"
                />
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              </div>
            </div>

            {/* Category */}
            <div className="mb-6">
              <label className="form-label">الفئة</label>
              <select
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                className="form-input"
              >
                <option value="">جميع الفئات</option>
                {categories?.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Price Range */}
            <div className="mb-6">
              <label className="form-label">نطاق السعر (جنيه)</label>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="number"
                  placeholder="من"
                  value={filters.minPrice}
                  onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                  className="form-input"
                />
                <input
                  type="number"
                  placeholder="إلى"
                  value={filters.maxPrice}
                  onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                  className="form-input"
                />
              </div>
            </div>

            {/* Sort */}
            <div className="mb-6">
              <label className="form-label">ترتيب حسب</label>
              <select
                value={filters.sort}
                onChange={(e) => handleFilterChange('sort', e.target.value)}
                className="form-input"
              >
                <option value="-created_at">الأحدث</option>
                <option value="created_at">الأقدم</option>
                <option value="price">السعر: من الأقل للأعلى</option>
                <option value="-price">السعر: من الأعلى للأقل</option>
                <option value="-rating">الأعلى تقييماً</option>
                <option value="-sales">الأكثر مبيعاً</option>
              </select>
            </div>

            {/* Clear Filters */}
            <button
              onClick={() => {
                setFilters({
                  search: '',
                  category: '',
                  minPrice: '',
                  maxPrice: '',
                  sort: '-created_at'
                });
                setSearchParams({});
              }}
              className="w-full text-center text-primary-600 hover:text-primary-700 text-sm"
            >
              مسح جميع الفلاتر
            </button>
          </div>
        </div>

        {/* Products Grid */}
        <div className="flex-1">
          {products.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {products.map((product) => (
                  <div key={product.id} className="product-card">
                    <Link to={`/products/${product.id}`}>
                      <div className="aspect-w-1 aspect-h-1 w-full overflow-hidden rounded-t-lg bg-gray-200">
                        <img
                          src={product.images?.[0]?.url || '/placeholder-product.jpg'}
                          alt={product.name}
                          className="h-48 w-full object-cover object-center hover:opacity-75 transition-opacity"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm font-medium text-gray-900 mb-2 line-clamp-2">
                          {product.name}
                        </h3>
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-lg font-bold text-primary-600">
                            {product.price ? parseFloat(product.price).toLocaleString() : '0'} جنيه
                          </p>
                          {product.compare_price && (
                            <p className="text-sm text-gray-500 line-through">
                              {parseFloat(product.compare_price).toLocaleString()} جنيه
                            </p>
                          )}
                        </div>
                        {product.vendor && (
                          <p className="text-xs text-gray-500 mb-2">
                            {product.vendor.store_name}
                          </p>
                        )}
                        {product.rating_average > 0 && (
                          <div className="flex items-center">
                            <span className="text-yellow-400 text-sm">★</span>
                            <span className="text-sm text-gray-600 mr-1">
                              {parseFloat(product.rating_average).toFixed(1)}
                            </span>
                            <span className="text-xs text-gray-500 mr-1">
                              ({product.rating_count || 0})
                            </span>
                          </div>
                        )}
                      </div>
                    </Link>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="flex justify-center items-center space-x-2 space-x-reverse">
                  <button
                    onClick={() => handlePageChange(pagination.current - 1)}
                    disabled={pagination.current === 1}
                    className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    السابق
                  </button>
                  
                  {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(pageNum => (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-3 py-2 border rounded-lg ${
                        pageNum === pagination.current
                          ? 'bg-primary-600 text-white border-primary-600'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  ))}
                  
                  <button
                    onClick={() => handlePageChange(pagination.current + 1)}
                    disabled={pagination.current === pagination.pages}
                    className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    التالي
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2M4 13h2m13-8l-4 4m0 0l-4-4m4 4V3" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                لا توجد منتجات
              </h3>
              <p className="text-gray-500">
                جرب تغيير معايير البحث أو الفلاتر
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Products;